import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Tracking } from '@panamax/app-state';
import { Product } from 'src/app/shared/models/product.model';
import { OrderHeader, OrderItem } from '@usf/ngrx-order';
import { SharedTrackingService } from 'src/app/shared/services/analytics/shared-tracking.service';
import { CONVERT_TO_WILL_CALL_TRACING_ACTIONS } from './convert-to-will-call-tracing.actions';
import { ConvertToWillCallTracingMessages } from './convert-to-will-call-tracing.constants';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';

@Injectable({
  providedIn: 'root',
})
export class ConvertToWillCallTracingService {
  traceConvertToWillCallClick = (
    orderHeader: OrderHeader,
    orderItems: OrderItem[],
    productMap: Map<number, Product>,
  ) => {
    const products = SharedTrackingService.tracingProductData(
      orderItems,
      productMap,
    );
    const tracking: Tracking = {
      tracing: {
        data: {
          traceContext: ConvertToWillCallTracingMessages.traceContext,
          isEndOfTrace: true,
          isStartOfTrace: true,
          attributes: {
            order: {
              orderId: orderHeader.orderId,
              origOrderId: !!orderHeader.origOrderId
                ? orderHeader.origOrderId
                : '',
              origSplitOrderId: !!orderHeader.origSplitOrderId
                ? orderHeader.origSplitOrderId
                : '',
              requestedDeliveryDate: orderHeader.requestedDeliveryDate,
              confirmedDeliveryDate: orderHeader.confirmedDeliveryDate,
              totalAmount: orderHeader.totalPrice,
              tandemOrderNumber: orderHeader.tandemOrderNumber,
            },
            event: ConvertToWillCallTracingMessages.convertToWillCall,
            products: products,
          },
        },
        transformFunc: genericTracingTransformer,
      },
    };

    this.store.dispatch(
      CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceConvertToWillCall({ tracking }),
    );
  };
  traceEditConvertToWillCallClick = (
    orderHeader: OrderHeader,
    orderItems: OrderItem[],
    productMap: Map<number, Product>,
  ) => {
    const products = SharedTrackingService.tracingProductData(
      orderItems,
      productMap,
    );
    const tracking: Tracking = {
      tracing: {
        data: {
          traceContext: ConvertToWillCallTracingMessages.traceContext,
          isEndOfTrace: true,
          isStartOfTrace: true,
          attributes: {
            order: {
              orderId: orderHeader.orderId,
              origOrderId: !!orderHeader.origOrderId
                ? orderHeader.origOrderId
                : '',
              origSplitOrderId: !!orderHeader.origSplitOrderId
                ? orderHeader.origSplitOrderId
                : '',
              requestedDeliveryDate: orderHeader.requestedDeliveryDate,
              confirmedDeliveryDate: orderHeader.confirmedDeliveryDate,
              tandemOrderNumber: orderHeader.tandemOrderNumber,
              totalAmount: orderHeader.totalPrice,
            },
            event: ConvertToWillCallTracingMessages.editConvertToWillCall,
            products: products,
          },
        },
        transformFunc: genericTracingTransformer,
      },
    };

    this.store.dispatch(
      CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceEditConvertToWillCall({
        tracking,
      }),
    );
  };

  traceSubmitWillCallFormModalDismiss = (
    orderHeader: OrderHeader,
    orderItems: OrderItem[],
    productMap: Map<number, Product>,
  ) => {
    const products = SharedTrackingService.analyticsProductData(
      orderItems,
      productMap,
      orderHeader?.divisionNumber,
    );
    const tracking: Tracking = {
      tracing: {
        data: {
          isEndOfTrace: false,
          isStartOfTrace: true,
          traceContext: ConvertToWillCallTracingMessages.traceContext,
          attributes: {
            event:
              ConvertToWillCallTracingMessages.willCallOrderConfirmationModalDismissEvent,
            modalName:
              ConvertToWillCallTracingMessages.willCallConfirmationModalName,
            products: products,
            order: {
              orderId: orderHeader?.orderId,
              tandemOrderNumber: orderHeader?.tandemOrderNumber,
              pickUpDateTime: orderHeader?.willCallDateTime,
              pickUpPersonName: orderHeader?.willCallPersonName,
            },
          },
        },
        transformFunc: genericTracingTransformer,
      },
    };
    this.store.dispatch(
      CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceSubmitWillCallFormModalDismiss({
        tracking,
      }),
    );
  };

  traceWillCallConfirmationModalCancelClick = (
    orderHeader: OrderHeader,
    pickUpDateTime: string | Date,
    pickUpPersonName: string,
  ) => {
    const tracking: Tracking = {
      tracing: {
        data: {
          traceContext: ConvertToWillCallTracingMessages.traceContext,
          isEndOfTrace: true,
          isStartOfTrace: true,
          attributes: {
            order: {
              orderId: orderHeader.orderId,
              origOrderId: !!orderHeader.origOrderId
                ? orderHeader.origOrderId
                : '',
              origSplitOrderId: !!orderHeader.origSplitOrderId
                ? orderHeader.origSplitOrderId
                : '',
              requestedDeliveryDate: orderHeader.requestedDeliveryDate,
              confirmedDeliveryDate: orderHeader.confirmedDeliveryDate,
              totalAmount: orderHeader.totalPrice,
              tandemOrderNumber: orderHeader.tandemOrderNumber,
              pickUpDateTime: pickUpDateTime,
              pickUpPersonName: pickUpPersonName,
            },
            event:
              ConvertToWillCallTracingMessages.willCallConfirmationModalCancelClick,
          },
        },
        transformFunc: genericTracingTransformer,
      },
    };
    this.store.dispatch(
      CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceWillCallConfirmationModalCancelClick(
        {
          tracking,
        },
      ),
    );
  };
  traceWillCallFormModalCancelClick = (
    orderHeader: OrderHeader,
    pickUpDateTime: string | Date,
    pickUpPersonName: string,
  ) => {
    const tracking: Tracking = {
      tracing: {
        data: {
          traceContext: ConvertToWillCallTracingMessages.traceContext,
          isEndOfTrace: true,
          isStartOfTrace: true,
          attributes: {
            order: {
              orderId: orderHeader.orderId,
              origOrderId: !!orderHeader.origOrderId
                ? orderHeader.origOrderId
                : '',
              origSplitOrderId: !!orderHeader.origSplitOrderId
                ? orderHeader.origSplitOrderId
                : '',
              requestedDeliveryDate: orderHeader.requestedDeliveryDate,
              confirmedDeliveryDate: orderHeader.confirmedDeliveryDate,
              totalAmount: orderHeader.totalPrice,
              tandemOrderNumber: orderHeader.tandemOrderNumber,
              pickUpDateTime: pickUpDateTime,
              pickUpPersonName: pickUpPersonName,
            },
            event:
              ConvertToWillCallTracingMessages.willCallFormModalCancelClick,
          },
        },
        transformFunc: genericTracingTransformer,
      },
    };
    this.store.dispatch(
      CONVERT_TO_WILL_CALL_TRACING_ACTIONS.traceWillCallFormModalCancelClick({
        tracking,
      }),
    );
  };

  constructor(private store: Store) {}
}
